import { useState } from 'react';
import type { Asociado } from '@lib/types';
import { CAMPO_OPCIONES } from '@lib/catalogs';
import { CodigoPostalCombobox } from './CodigoPostalCombobox';
import { FIELDS, validarFormTitular } from './asociado-campos';

interface Props {
  asociado: Asociado;
  onClose: () => void;
  onSave: (patch: Partial<Asociado>) => Promise<void>;
}

export function EditAsociadoModal({ asociado, onClose, onSave }: Props) {
  const [form, setForm] = useState<Record<string, string>>(() => {
    const acc: Record<string, string> = {};
    FIELDS.forEach((f) => {
      acc[f.key as string] = (asociado[f.key] ?? '').toString();
    });
    return acc;
  });
  const [saving, setSaving] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const update = (k: string, v: string) => {
    setForm((p) => ({ ...p, [k]: v }));
    if (validationErrors[k]) {
      setValidationErrors((p) => {
        const next = { ...p };
        delete next[k];
        return next;
      });
    }
  };

  // Si estamos editando un titular (parentesco='0') aplicamos las reglas
  // obligatorias de email + celular. Para el resto del grupo familiar el
  // backend ya tiene su propio juego de validaciones (row.validator) y no
  // requieren celular.
  const esTitular = (form.parentesco ?? asociado.parentesco) === '0';

  const handleSave = async () => {
    const errores = esTitular ? validarFormTitular(form) : {};
    setValidationErrors(errores);
    if (Object.keys(errores).length > 0) return;

    setSaving(true);
    try {
      const patch = Object.fromEntries(
        Object.entries(form).filter(([, v]) => v !== ''),
      );
      await onSave(patch as Partial<Asociado>);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modalBackdrop" onClick={onClose}>
      <div className="modalCard" onClick={(e) => e.stopPropagation()}>
        <header className="modalHeader">
          <div>
            <h2 className="heading-5">Editar fila #{asociado.rowNumber}</h2>
            <p className="text-element-mid">
              Las validaciones se re-ejecutan al guardar.
            </p>
          </div>
          <button className="modalClose" onClick={onClose}>
            ×
          </button>
        </header>

        {asociado.errores.length > 0 && (
          <div className="modalErrors">
            <p className="status status-error">
              {asociado.errores.length} error{asociado.errores.length === 1 ? '' : 'es'}
            </p>
            <ul>
              {asociado.errores.map((e, i) => (
                <li key={i}>
                  {e.field ? <strong>{e.field}: </strong> : null}
                  {e.message}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="modalGrid">
          {FIELDS.map((f) => {
            const k = f.key as string;
            const hasBackendError = asociado.errores.some((e) => e.field === f.key);
            const validationError = validationErrors[k];
            const hasError = hasBackendError || !!validationError;
            const opciones = CAMPO_OPCIONES[k];
            const value = form[k] ?? '';
            // Solo el email es obligatorio para el titular; celular es opcional
            // (si lo cargan deben ser ambos campos llenos, pero el asterisco
            // se reserva para los campos que NO admiten vacío).
            const labelExtra = esTitular && k === 'email' ? ' *' : '';
            if (k === 'cp') {
              // CP usa combobox con typeahead contra el catálogo de Binary.
              return (
                <CodigoPostalCombobox
                  key={k}
                  label={`${f.label}${labelExtra}`}
                  value={value}
                  onChange={(cp) => update(k, cp)}
                  error={validationError}
                />
              );
            }
            return (
              <label key={k} className="formField">
                <span>{f.label}{labelExtra}</span>
                <CampoControl
                  fieldKey={k}
                  value={value}
                  placeholder={f.placeholder}
                  hasError={hasError}
                  opciones={opciones}
                  onChange={(v) => update(k, v)}
                />
                {validationError && (
                  <span className="status status-error" style={{ display: 'block', fontSize: 12, marginTop: 4 }}>
                    {validationError}
                  </span>
                )}
              </label>
            );
          })}
        </div>

        <footer className="modalFooter">
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <span className="btn-font-style">{saving ? 'Guardando…' : 'Guardar'}</span>
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Render del control adecuado para el campo:
 *  - Si el campo es enum (sexo, parentesco, tipoDoc, nacionalidad, estadoCivil) →
 *    `<select>` con las opciones del catálogo. Imposible tipear un valor inválido.
 *  - Resto → `<input>` con el placeholder. El backend valida igualmente.
 *
 * Si el valor actual NO está en el catálogo (caso edge: el padrón vino con un
 * código viejo o malformado), se agrega una opción "no válido" señalada para que
 * el admin lo vea y lo corrija.
 */
export function CampoControl({
  fieldKey,
  value,
  placeholder,
  hasError,
  opciones,
  onChange,
}: {
  fieldKey: string;
  value: string;
  placeholder?: string;
  hasError: boolean;
  opciones?: Array<{ value: string; label: string }>;
  onChange: (v: string) => void;
}) {
  const claseBase = hasError ? 'input input-error' : 'input';

  if (opciones && opciones.length > 0) {
    const valorEnCatalogo = opciones.some((o) => o.value === value);
    return (
      <select
        className={claseBase}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-field={fieldKey}
      >
        <option value="">— Sin asignar —</option>
        {opciones.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label} ({o.value})
          </option>
        ))}
        {value && !valorEnCatalogo && (
          <option value={value} disabled>
            {value} (no válido)
          </option>
        )}
      </select>
    );
  }

  return (
    <input
      className={claseBase}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      data-field={fieldKey}
    />
  );
}
